import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent, type AccentKey } from './theme';

export interface MetricItem {
  icon: string;
  title: string;
  desc: string;
}

export interface DashboardRow {
  label: string;
  value: string;
  pct: number;
}

interface SolutionMetricsProps {
  eyebrow: string;
  title: string;
  items: MetricItem[];
  accentKey: AccentKey;
  dashboard?: {
    title: string;
    rows: DashboardRow[];
    note: string;
  };
}

export default function SolutionMetrics({ eyebrow, title, items, accentKey, dashboard }: SolutionMetricsProps) {
  const a = accent(accentKey);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={eyebrow} title={title} />

        <Reveal className="mt-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-lg bg-background-100 border border-background-200 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-md ${a.iconBox}`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="mt-4 font-heading font-bold text-foreground-950 text-base leading-snug">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {dashboard && (
          <Reveal className="mt-10" delay={100}>
            <div className="p-6 md:p-8 rounded-lg bg-foreground-950">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-bold text-background-50 text-lg">{dashboard.title}</h3>
                <span className="px-3 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-xs text-foreground-300 whitespace-nowrap">
                  示意界面
                </span>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {dashboard.rows.map((row) => (
                  <div key={row.label}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-300">{row.label}</span>
                      <span className="text-sm font-semibold text-background-50 whitespace-nowrap">{row.value}</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-background-50/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent-500"
                        style={{ width: `${Math.min(Math.max(row.pct, 0), 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-foreground-400 leading-relaxed">{dashboard.note}</p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}